// src/utils/launchCoverage.js
//
// NOTE (WP6, docs/mcp-signoff-spec.md §6/§9): the AUTHORITATIVE sign-off
// coverage/verdict now lives on the proxy (proxy-server/launch/coverage.js —
// a verbatim CommonJS port of this file — composed into buildRollup.js),
// served by GET /proxy/launches/<id>/rollup. LaunchPage.jsx reads that
// endpoint's `coverage`/`criteria[].coverage` instead of calling
// launchCoverage()/criterionCoverage() from here for anything it DISPLAYS as
// the launch's sign-off state. This copy survives ONLY because CapturePanel.jsx
// needs a "is capture complete?" answer over IN-FLIGHT results that are not
// yet recorded/attached on the proxy (an unrecorded `record:false` live
// checklist run every tick) — a genuinely different question the rollup
// endpoint (reads only recorded runs) cannot answer, and it drives the
// auto-finalize decision loop client-side in real time.
// Any RULE change here (the coverage-tier logic) must be mirrored in
// proxy-server/launch/coverage.js or the two copies silently drift — see
// docs/mcp-signoff-spec.md §4's shared-fixture mitigation.
//
// Coverage-guided capture support (WP0, docs/coverage-guided-capture-spec.md §4).
// Pure functions to track traffic evidence against acceptance criteria during a
// capture session. Given a launch's criteria and the latest check results from a
// /proxy/checks/run call, decides which criteria have evidence, which are waiting,
// and whether the launch is "complete" (ready for auto-finalize: all non-manual
// criteria covered + at least one non-manual exists).
//
// Evidence tier (checkCoverage):
//   - 'none': result absent or matched===0
//   - 'matched': matched > 0 && found === 0 (traffic targeted but nothing read)
//   - 'evidenced': found > 0 (actual value observed)
//
// Criterion state (criterionCoverage):
//   - 'manual': mapping === 'manual' (never provable via traffic)
//   - 'unmapped': no checkIds / mapping === 'unmapped'
//   - 'waiting': all checks have evidence 'none'
//   - 'partial': some checks ≥ 'matched', not all
//   - 'covered': all checks ≥ 'matched'
//
// Launch completion (launchCoverage):
//   - complete iff every non-manual, non-unmapped criterion is covered
//     AND at least one criterion is non-manual/non-unmapped
//   - Never throws; tolerate garbage input (non-array criteria, missing results, etc.)

/**
 * checkCoverage(result) -> 'none' | 'matched' | 'evidenced'
 *
 * @param {Object|null|undefined} result - check result from evaluateCheck(),
 *   shape: { matched, found, ... }
 * @returns {string} coverage tier
 */
export function checkCoverage(result) {
  try {
    if (!result || typeof result !== 'object') return 'none';
    const matched = result.matched;
    const found = result.found;
    if (typeof matched !== 'number' || matched === 0) return 'none';
    if (typeof found !== 'number' || found === 0) return 'matched';
    return 'evidenced';
  } catch {
    return 'none';
  }
}

/**
 * criterionCoverage(criterion, resultsByCheckId) -> coverage object
 *
 * @param {Object} criterion - from launch.criteria[],
 *   shape: { id, ac, checkIds, mapping, ... }
 * @param {Object|Map|null} resultsByCheckId - lookup of checkId -> result
 * @returns {Object} { state, perCheck, evidencedCount }
 *   - state: 'manual' | 'unmapped' | 'waiting' | 'partial' | 'covered'
 *   - perCheck: [{checkId, coverage}] for each checkId
 *   - evidencedCount: how many checks have 'evidenced' coverage (for UI)
 */
export function criterionCoverage(criterion, resultsByCheckId) {
  try {
    if (!criterion || typeof criterion !== 'object') {
      return {
        state: 'unmapped',
        perCheck: [],
        evidencedCount: 0,
      };
    }

    // Manual criteria can never be proven via traffic.
    if (criterion.mapping === 'manual') {
      return {
        state: 'manual',
        perCheck: [],
        evidencedCount: 0,
      };
    }

    // Extract checkIds, filter out falsy values (null, undefined, '').
    const checkIds = Array.isArray(criterion.checkIds)
      ? criterion.checkIds.filter(Boolean)
      : [];

    // No checkIds or unmapped -> unmapped state.
    if (!checkIds.length || criterion.mapping === 'unmapped') {
      return {
        state: 'unmapped',
        perCheck: [],
        evidencedCount: 0,
      };
    }

    // Lookup coverage for each checkId.
    const perCheck = checkIds.map((checkId) => {
      const result = lookupResult(resultsByCheckId, checkId);
      return {
        checkId,
        coverage: checkCoverage(result),
      };
    });

    // Tally coverage states.
    const coverages = perCheck.map((pc) => pc.coverage);
    const noneCount = coverages.filter((c) => c === 'none').length;
    const matchedCount = coverages.filter((c) => c === 'matched').length;
    const evidencedCount = coverages.filter((c) => c === 'evidenced').length;

    let state;
    if (noneCount === coverages.length) {
      // All checks have no evidence.
      state = 'waiting';
    } else if (noneCount > 0) {
      // Some checks have evidence, some don't.
      state = 'partial';
    } else if (matchedCount > 0 || evidencedCount > 0) {
      // All checks have at least 'matched' coverage (none are 'none').
      state = 'covered';
    } else {
      // Fallback (shouldn't happen, but safety).
      state = 'waiting';
    }

    return {
      state,
      perCheck,
      evidencedCount,
    };
  } catch {
    return {
      state: 'unmapped',
      perCheck: [],
      evidencedCount: 0,
    };
  }
}

/**
 * launchCoverage(criteria, resultsByCheckId) -> coverage rollup
 *
 * @param {Array} criteria - launch.criteria (or non-array)
 * @param {Object|Map|null} resultsByCheckId - lookup of checkId -> result
 * @returns {Object} { covered, partial, waiting, manual, unmapped, complete }
 *   - complete: true iff every non-manual, non-unmapped criterion is 'covered'
 *     AND at least one criterion is non-manual/non-unmapped
 *   - Never throws
 */
export function launchCoverage(criteria, resultsByCheckId) {
  const out = {
    covered: 0,
    partial: 0,
    waiting: 0,
    manual: 0,
    unmapped: 0,
    complete: false,
  };

  try {
    if (!Array.isArray(criteria)) {
      return out;
    }

    let hasNonManualNonUnmapped = false;
    let allNonManualNonUnmappedCovered = true;

    for (const criterion of criteria) {
      const { state } = criterionCoverage(criterion, resultsByCheckId);

      if (state === 'covered') out.covered += 1;
      else if (state === 'partial') out.partial += 1;
      else if (state === 'waiting') out.waiting += 1;
      else if (state === 'manual') out.manual += 1;
      else if (state === 'unmapped') out.unmapped += 1;

      // Track completeness: only non-manual, non-unmapped criteria matter.
      if (state !== 'manual' && state !== 'unmapped') {
        hasNonManualNonUnmapped = true;
        if (state !== 'covered') {
          allNonManualNonUnmappedCovered = false;
        }
      }
    }

    // Complete iff:
    //  1. At least one non-manual, non-unmapped criterion exists
    //  2. Every such criterion is 'covered'
    out.complete =
      hasNonManualNonUnmapped && allNonManualNonUnmappedCovered;
  } catch {
    // Never throw; return seeded defaults with complete=false.
  }

  return out;
}

/**
 * Helper: lookup a result in a plain object or Map.
 * Never throws.
 */
function lookupResult(resultsByCheckId, checkId) {
  if (!resultsByCheckId || !checkId) return null;
  try {
    if (typeof resultsByCheckId.get === 'function') {
      return resultsByCheckId.get(checkId) || null;
    }
    return resultsByCheckId[checkId] || null;
  } catch {
    return null;
  }
}

export default checkCoverage;
